// src/components/modals/MiCuentaModal.jsx
//
// Modal "Mi cuenta": muestra avatar grande + nombre/email, plan actual
// (con badge Activo + botón "Cambiar o renovar plan" → /paywall),
// estado de Telegram, y información del usuario (miembro desde + último
// pago).
//
// Datos de userData (Firestore /users/{uid}, camelCase):
//   nombre, email, role, accessUntil, lastPaymentPlan, lastPaymentDate,
//   lastPaymentAmount, telegramChatId, telegramUsername, fechaCreacion
//
// Fallbacks (cuentas legacy):
//   nombre        → email.split('@')[0]
//   fechaCreacion → "—"
//   lastPayment*  → "—" (o ocultar fila si todos null)
//   telegramChatId null → badge "Desconectado"

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useAuth } from '../../auth/AuthContext';

const PLAN_LABELS = {
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  biannual: 'Semestral',
};

function fmtDateES(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Santiago' });
}

function fmtCLP(n) {
  if (n == null) return '—';
  try {
    return '$' + Number(n).toLocaleString('es-CL');
  } catch {
    return '$' + n;
  }
}

const I = ({ children, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{children}</svg>
);
const IconClose = () => <I size={18}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></I>;
const IconTelegram = () => (
  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor">
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
  </svg>
);

export default function MiCuentaModal({ open, onClose }) {
  const { user, userData, isAdmin, hasAccess } = useAuth();
  const navigate = useNavigate();

  // Cancelación de suscripción (discreta, estilo Netflix).
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelStatus, setCancelStatus] = useState('idle'); // idle | loading | done | error

  // ESC cierra
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const nombre =
    userData?.nombre ||
    user?.displayName ||
    (user?.email ? user.email.split('@')[0] : 'Usuario');
  const email = userData?.email || user?.email || '';
  const initials = (nombre || 'EE').slice(0, 2).toUpperCase();

  // Cuenta de cortesía (acceso permanente sin pago, ej. palos blancos).
  // compAccess no lo lee el backend de acceso (ese usa accessUntil); acá solo
  // ajusta la UI para que no se vea "rota" con fecha 2099 / plan "—".
  const isComp = userData?.compAccess === true;
  // En trial todavía no hay lastPaymentPlan → fallback a trial_plan_id para
  // mostrar "Mensual" en vez de "—".
  const planId = userData?.lastPaymentPlan || userData?.trial_plan_id || null;
  const planLabel = isComp
    ? 'Cortesía'
    : (planId ? PLAN_LABELS[planId] || planId : (isAdmin ? 'Admin' : '—'));
  // "Renueva el" / "Acceso hasta" = próximo cobro real (next_invoice_date).
  // accessUntil queda alineado tras el fix; nextChargeAt es la fuente directa.
  const accessUntil = userData?.nextChargeAt || userData?.accessUntil;
  const isActive = isAdmin || hasAccess;

  const betaInfo = (() => {
    const beta = userData?.betaAccess;
    if (!beta || beta.active !== true) return { show: false };
    const expMs = beta.expiresAt?.seconds
      ? beta.expiresAt.seconds * 1000
      : (beta.expiresAt ? new Date(beta.expiresAt).getTime() : null);
    if (!expMs) return { show: false };
    const now = Date.now();
    const expired = expMs <= now;
    const daysLeft = Math.max(0, Math.ceil((expMs - now) / 86400000));
    return { show: true, expired, daysLeft, expiresAt: beta.expiresAt };
  })();

  const telegramConnected = Boolean(userData?.telegramChatId);
  const telegramUsername = userData?.telegramUsername;

  const fechaCreacion = userData?.fechaCreacion;
  const lastPaymentAmount = userData?.lastPaymentAmount;
  const lastPaymentDate = userData?.lastPaymentDate;
  const showUltimoPago = lastPaymentAmount != null || lastPaymentDate != null;

  const handleChangePlan = () => {
    onClose?.();
    navigate('/paywall');
  };

  const handleConnectTelegram = () => {
    onClose?.();
    navigate('/conectar-telegram');
  };

  // Suscripción: estado y elegibilidad para cancelar.
  const subStatus = userData?.subscription_status;
  const canceledAtPeriodEnd = cancelStatus === 'done' || subStatus === 'CANCELED_AT_PERIOD_END';
  // Solo ofrecemos cancelar si hay una suscripción nativa activa (trial o pagada)
  // y todavía no fue cancelada.
  const hasActiveSub =
    !isAdmin &&
    Boolean(userData?.flowSubscriptionId) &&
    (subStatus === 'TRIAL' || subStatus === 'PAID') &&
    !canceledAtPeriodEnd;

  const handleCancelSubscription = async () => {
    setCancelStatus('loading');
    try {
      const callable = httpsCallable(functions, 'flowCancelSubscription');
      await callable();
      setCancelStatus('done');
      setShowCancelConfirm(false);
    } catch (err) {
      console.error('[MiCuenta] cancel error:', err);
      setCancelStatus('error');
    }
  };

  return createPortal(
    <div
      className="dm-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="dm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dm-mc-title"
      >
        <div className="dm-modal-header">
          <h2 className="dm-modal-title" id="dm-mc-title">Mi cuenta</h2>
          <button className="dm-modal-close" onClick={onClose} aria-label="Cerrar" type="button">
            <IconClose />
          </button>
        </div>

        <div className="dm-account-head">
          <div className="dm-avatar-lg">{initials}</div>
          <div className="who">
            <div className="name">{nombre}</div>
            {email && <div className="email">{email}</div>}
          </div>
        </div>

        {/* PLAN ACTUAL */}
        <div className="dm-section-label">Plan actual</div>
        <div className="dm-card">
          <div className="dm-row">
            <span className="k">Plan</span>
            <span className="v">{planLabel}</span>
          </div>
          {!isAdmin && lastPaymentAmount != null && (
            <div className="dm-row">
              <span className="k">Monto</span>
              <span className="v num">{fmtCLP(lastPaymentAmount)}</span>
            </div>
          )}
          {!isAdmin && (
            <div className="dm-row">
              <span className="k">{isComp ? 'Acceso' : (canceledAtPeriodEnd ? 'Acceso hasta' : 'Renueva el')}</span>
              <span className="v">{isComp ? 'Permanente' : fmtDateES(accessUntil)}</span>
            </div>
          )}
          <div className="dm-row">
            <span className="k">Estado</span>
            <span className="v">
              <span className={`dm-badge ${isActive ? 'active' : 'muted'}`}>
                <span className="dot" />
                {canceledAtPeriodEnd ? 'Activo · No se renueva' : (isActive ? 'Activo' : 'Sin acceso')}
              </span>
            </span>
          </div>
        </div>
        {!isAdmin && !isComp && (
          <button className="dm-btn-primary" onClick={handleChangePlan} type="button">
            Cambiar o renovar plan
          </button>
        )}

        {betaInfo.show && (
          <>
            <div className="dm-section-label">Acceso beta</div>
            <div className="dm-card">
              <div className="dm-row">
                <span className="k">Estado</span>
                <span className="v">
                  <span className={`dm-badge ${betaInfo.expired ? 'muted' : 'active'}`}>
                    <span className="dot" />
                    {betaInfo.expired ? 'Beta expirada' : `BETA · Te quedan ${betaInfo.daysLeft} día${betaInfo.daysLeft !== 1 ? 's' : ''}`}
                  </span>
                </span>
              </div>
              <div className="dm-row">
                <span className="k">Vence el</span>
                <span className="v">{fmtDateES(betaInfo.expiresAt)}</span>
              </div>
            </div>
            {!isAdmin && (
              <button className="dm-btn-primary" onClick={handleChangePlan} type="button" style={{ marginTop: 8 }}>
                Suscríbete para no perder acceso
              </button>
            )}
          </>
        )}

        {/* CONEXIÓN */}
        <div className="dm-section-label">Conexión</div>
        <div className="dm-card">
          <div className="dm-row" style={{ alignItems: 'center' }}>
            <span className="k" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: 'var(--info, #229ED9)', display: 'inline-flex' }}><IconTelegram /></span>
              Telegram
            </span>
            <span className="v">
              {telegramConnected ? (
                <>
                  {telegramUsername && (
                    <span style={{ color: 'var(--muted, #6E6E73)', marginRight: 8, fontWeight: 400 }}>
                      @{telegramUsername}
                    </span>
                  )}
                  <span className="dm-badge active"><span className="dot" />Conectado</span>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectTelegram}
                  style={{
                    background: 'transparent',
                    border: 0,
                    padding: 0,
                    color: 'var(--accent)',
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    textDecoration: 'underline',
                  }}
                >
                  Conectar
                </button>
              )}
            </span>
          </div>
        </div>

        {/* INFORMACIÓN */}
        <div className="dm-section-label">Información</div>
        <div className="dm-card">
          <div className="dm-row">
            <span className="k">Miembro desde</span>
            <span className="v">{fmtDateES(fechaCreacion)}</span>
          </div>
          {showUltimoPago && (
            <div className="dm-row">
              <span className="k">Último pago</span>
              <span className="v">
                {fmtCLP(lastPaymentAmount)}
                {lastPaymentDate && (
                  <span style={{ color: 'var(--muted, #6E6E73)', fontWeight: 400, marginLeft: 6 }}>
                    · {fmtDateES(lastPaymentDate)}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Cancelar suscripción — discreto, al final (estilo Netflix) */}
        {hasActiveSub && (
          <button
            type="button"
            onClick={() => { setCancelStatus('idle'); setShowCancelConfirm(true); }}
            style={{
              display: 'block', margin: '18px auto 2px', background: 'transparent',
              border: 0, padding: 4, color: 'var(--muted, #6E6E73)', fontSize: 12.5,
              cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
            }}
          >
            Cancelar suscripción
          </button>
        )}
        {cancelStatus === 'done' && (
          <p style={{ fontSize: 12.5, color: 'var(--v-enter, #16A34A)', textAlign: 'center', margin: '14px 0 0', lineHeight: 1.5 }}>
            Tu suscripción se canceló. Tienes acceso hasta el {fmtDateES(accessUntil)}.
          </p>
        )}
      </div>

      {/* Confirmación de cancelación */}
      {showCancelConfirm && (
        <div
          onMouseDown={(e) => { if (e.target === e.currentTarget && cancelStatus !== 'loading') setShowCancelConfirm(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(2px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        >
          <div style={{ background: 'var(--bg-elev, #fff)', border: '1px solid var(--line)', borderRadius: 16, padding: 24, maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 19, fontWeight: 700, margin: '0 0 10px' }}>
              ¿Cancelar tu suscripción?
            </h3>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.5, margin: '0 0 20px' }}>
              Mantienes el acceso hasta el {fmtDateES(accessUntil)}. Después no se te cobrará de nuevo.
            </p>
            {cancelStatus === 'error' && (
              <p style={{ fontSize: 12.5, color: 'var(--neg)', margin: '0 0 14px' }}>
                No pudimos cancelar. Intenta de nuevo.
              </p>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelStatus === 'loading'}
                style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid var(--line)', background: 'transparent', color: 'var(--ink)', fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Volver
              </button>
              <button
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelStatus === 'loading'}
                style={{ flex: 1, padding: 12, borderRadius: 10, border: 0, background: 'var(--neg)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: cancelStatus === 'loading' ? 'default' : 'pointer', fontFamily: 'inherit', opacity: cancelStatus === 'loading' ? 0.6 : 1 }}
              >
                {cancelStatus === 'loading' ? 'Cancelando…' : 'Sí, cancelar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
